import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import Spinner from 'components/shared/Spinner';
import STEPS from 'constants/steps';
import { verifyEmailReturn } from 'reduxConfig/auth.creators';
import { getCardPartnershipCodeFromLs } from '@nbx/frontend-helpers/services';
import { isMobileWebBrowser } from 'helpers/utils';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;
/**
 * This is the page users see when being redirected from their email verification link back to nbx.com
 */
function VerifyEmailReturn({ dispatch, loading, error, step, actionCode, isMobile }) {
  const { t } = useTranslation();
  const [initial, setInitial] = useState(true);
  const [cardPartnershipCode, setCardPartnershipCode] = useState();

  useEffect(() => {
    getCardPartnershipCodeFromLs()
      .then(code => code ?? new URLSearchParams(window.location.search).get('cardPartnershipCode'))
      .then(setCardPartnershipCode);
  }, []);

  useEffect(() => {
    dispatch(verifyEmailReturn(actionCode));
    setInitial(false);
  }, [dispatch, actionCode]);

  useEffect(() => {
    if (
      !loading &&
      step === STEPS.verifyEmailComplete &&
      !error &&
      isMobileWebBrowser &&
      cardPartnershipCode
    ) {
      // eslint-disable-next-line no-console
      console.log('[VerifyEmailReturn] redirect to install...');
      window.location.href = '/install';
    }
  }, [loading, step, error, cardPartnershipCode]);

  let title = t('auth_action_verify_success');
  let blurb = t('auth_action_verify_continue');

  if (loading || initial) {
    title = t('auth_action_verify_loading');
    blurb = null;
  }

  if (error) {
    title = t('error_title_generic');
    blurb = t(error);
  }

  return (
    <>
      <SEO title={t('auth_verify_action_meta_title')} />
      <Heading tag="h1">{title}</Heading>

      {blurb && (
        <Text tag="p" muted styles={[styles.belowHeaderText]}>
          {blurb}
        </Text>
      )}

      {loading && (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      )}

      {step === STEPS.verifyEmailComplete && !error && (
        <>
          {isMobileWebBrowser && (
            <Row styles={[styles.paddedRow]}>
              <ButtonPrimary fullWidth={isMobile} href="/install" target="_self">
                {t('auth_action_install')}
              </ButtonPrimary>
            </Row>
          )}

          {(!cardPartnershipCode || !isMobileWebBrowser) && (
            <Row styles={[styles.paddedRow]}>
              <ButtonPrimary fullWidth={isMobile} to={microapps.routes.login.path}>
                {t('auth_action_continue')}
              </ButtonPrimary>
            </Row>
          )}
        </>
      )}
    </>
  );
}

VerifyEmailReturn.propTypes = {
  actionCode: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.string,
  isMobile: PropTypes.bool.isRequired
};

VerifyEmailReturn.defaultProps = {
  actionCode: null,
  error: null,
  step: null
};

export default VerifyEmailReturn;
